import { useFetchers } from "@website/fetcher/use-fetcher";
import { getTracking } from "@website/store/tracking";
import { createQueryString } from "@website/utils/query-params/createQueryString";
import { useRef, useState } from "react";
import { searchSolutionsService } from "./index.services";
import { ResponseSearchResultType, SearchRequestData } from "./types";
interface Properties {
  origin: string;
  dest: string;
  rtDate: string;
  stDate: string;
  passengers: Array<{
    adultCount: number;
    children: Array<number>;
  }>;
  passengersParameter: string;
  filterParameters: {
    order_by: string;
    hotel_name: string;
    stars: string;
    rate: string;
    facilities: string;
    price_from: string;
    price_to: string;
    transport_types: string;
  };
  ssrResults: ResponseSearchResultType["data"] | null;
}
export function useSolutions({
  ssrResults,
  origin,
  dest,
  rtDate,
  stDate,
  passengersParameter,
  passengers,
  filterParameters
}: Properties) {
  const {
    order_by,
    hotel_name,
    stars,
    rate,
    facilities,
    price_to,
    price_from,
    transport_types
  } = filterParameters;
  const pageKey = createQueryString({
    params: {
      stDate,
      rtDate,
      origin,
      dest,
      passengersParameter,
      ...filterParameters
    }
  });
  const [pages, setPages] = useState<Record<string, Array<number>>>({});
  const loadPages = pages[pageKey] || [1];
  const availableFilters = useRef([]);
  const csrSolutionsData = useFetchers({
    queries: loadPages.map(page => ({
      retry: 1,
      queryKey: ["tour-results", page, pageKey],
      enabled: !ssrResults,
      queryFn: () => {
        let filter: SearchRequestData["filter"] = {
          page
        };
        if (transport_types) {
          filter = {
            ...filter,
            transportTypes: transport_types.split(",")
          };
        }
        if (price_from) {
          filter = {
            ...filter,
            minPrice: Number(`${price_from}0`)
          };
        }
        if (price_to) {
          filter = {
            ...filter,
            maxPrice: Number(`${price_to}0`)
          };
        }
        if (stars) {
          filter = {
            ...filter,
            stars: stars.split(",").map(Number)
          };
        }
        if (rate) {
          filter = {
            ...filter,
            rate
          };
        }
        if (facilities) {
          filter = {
            ...filter,
            facilities: facilities.split(",").map(Number)
          };
        }
        if (order_by) filter = {
          ...filter,
          sort: order_by
        };
        if (hotel_name) filter = {
          ...filter,
          hotelName: hotel_name
        };
        const {
          aid
        } = getTracking();
        return searchSolutionsService({
          origin,
          destination: dest,
          departureDate: stDate,
          returnDate: rtDate,
          rooms: passengers,
          filter,
          aid
        });
      }
    }))
  });
  const {
    isError,
    data: firstResponseData
  } = csrSolutionsData[0] || {};
  const {
    filters
  } = ssrResults ?? firstResponseData ?? {};
  const totalSolutionsCount = (ssrResults ?? firstResponseData)?.pagination?.total_results;
  function onNextPage() {
    setPages({
      [pageKey]: [...loadPages, loadPages.at(-1) + 1]
    });
  }
  if (filters?.length) availableFilters.current = filters;
  const csrSolutions = csrSolutionsData.flatMap(({
    data
  }) => data?.solutions ?? []);
  const useInitialSsrData = ssrResults && csrSolutions.length === 0;
  const isLoading_0 = useInitialSsrData ? false : csrSolutionsData.some(({
    isLoading
  }) => isLoading);
  const solutions = useInitialSsrData ? ssrResults.solutions : csrSolutions;
  const remainSolutionsCount = totalSolutionsCount - solutions.length;
  return {
    totalSolutionsCount,
    solutions,
    availableFilters: availableFilters.current,
    remainSolutionsCount,
    onNextPage,
    isLoading: isLoading_0,
    isError
  };
}